import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessControlStyles } from '../harness.styles.js';
import type { IHarnessMessageViewState, IHarnessToolCall } from '../interfaces.js';
import { DeesHarnessToolRegistry, parseMcpName } from '../toolregistry.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-tool-fullscreen': DeesHarnessToolFullscreen;
  }
}

/**
 * Fullscreen view of one tool card, appended to document.body by
 * DeesHarnessToolCard so it escapes every containment context
 * (content-visibility, transforms) that would trap a position: fixed layer
 * inside the transcript. Emits `tool-fullscreen-close` on Escape, backdrop
 * press, or the minimize button; the owning card removes it.
 */
@customElement('dees-harness-tool-fullscreen')
export class DeesHarnessToolFullscreen extends DeesElement {
  @property({ attribute: false })
  accessor call: IHarnessToolCall = { id: '', name: '', status: 'pending' };

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  /** Follows the owning card, so a subagent thread parses markdown under the host's policy. */
  @property({ type: Boolean })
  accessor markdownWhileStreaming: boolean = true;

  /**
   * The row's retained state, so the fullscreen view reads and writes the same record: a subagent
   * thread keeps what the reader left in it, and a reopened view is where they were.
   */
  @property({ attribute: false })
  accessor viewState: IHarnessMessageViewState | undefined = undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessControlStyles,
    css`
      :host {
        position: fixed;
        inset: 0;
        z-index: 4000;
        display: grid;
        place-items: center;
        padding: 4vh 3vw;
        background: var(--dees-color-scrim);
        font-family: var(--dees-font-family);
      }

      .panel {
        display: flex;
        flex-direction: column;
        width: min(1200px, 100%);
        height: 100%;
        min-height: 0;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-2xl);
        background: var(--dees-color-bg-primary);
        box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
        overflow: hidden;
      }

      .panelHeader {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        gap: var(--dees-spacing-sm);
        min-height: 44px;
        box-sizing: border-box;
        padding: 8px 16px;
        background: var(--dees-color-bg-canvas);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        color: var(--dees-color-text-primary);
      }

      .panelTitle {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13px;
        font-weight: 600;
      }

      .panelClose {
        display: grid;
        place-items: center;
        margin-left: auto;
        padding: 6px;
        width: 28px;
        height: 28px;
        border: 0;
        border-radius: var(--dees-radius-md);
        background: transparent;
        color: var(--dees-color-text-secondary);
        cursor: pointer;
      }

      .panelClose:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      .panelBody {
        flex: 1;
        min-height: 0;
        overflow: auto;
        padding: var(--dees-spacing-md);
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      .panelBody dees-harness-tool-card {
        display: block;
      }

      /* A subagent thread scrolls inside its own transcript, so the panel hands it the height. */
      .panelBody.threadBody {
        display: flex;
        flex-direction: column;
        padding: 0;
        overflow: hidden;
      }

      .panelBody.threadBody dees-harness-tool-card {
        flex: 1;
        min-height: 0;
      }
    `,
  ];

  public render(): TemplateResult {
    const parsed = this.call.mcp ? undefined : parseMcpName(this.call.name);
    const enriched = parsed
      ? { ...this.call, mcp: { serverName: parsed.serverName, toolName: parsed.toolName } }
      : this.call;
    const descriptor = this.toolRegistry.resolve(enriched);
    const label = typeof descriptor.label === 'function' ? descriptor.label(enriched) : descriptor.label;
    const subtitle = descriptor.subtitle?.(enriched) ?? '';
    const threadView = descriptor.kind === 'subtask' && this.call.subtask?.previewState === 'ready';
    return html`
      <div class="panel" role="dialog" aria-modal="true" aria-label=${label} @click=${(event: Event) => event.stopPropagation()}>
        <header class="panelHeader">
          <dees-icon .icon=${descriptor.icon} iconSize="15"></dees-icon>
          <span class="panelTitle">${label}${subtitle ? ` · ${subtitle}` : ''}</span>
          <button class="panelClose" type="button" aria-label="Close tool inspection" @click=${this.emitClose}>
            <dees-icon icon="lucide:Minimize2" iconSize="15"></dees-icon>
          </button>
        </header>
        <div class=${`panelBody ${threadView ? 'threadBody' : ''}`}>
          <dees-harness-tool-card
            .call=${this.call}
            .toolRegistry=${this.toolRegistry}
            .expanded=${true}
            .outputExpanded=${true}
            .showTimestamp=${true}
            .markdownWhileStreaming=${this.markdownWhileStreaming}
            .viewState=${this.viewState}
            uncapped
          ></dees-harness-tool-card>
        </div>
      </div>
    `;
  }

  public firstUpdated(): void {
    window.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('click', (event) => {
      if (event.target === this) this.emitClose();
    });
    this.shadowRoot?.querySelector<HTMLButtonElement>('.panelClose')?.focus();
  }

  /** Re-render after the host updates or mutates the canonical call in place. */
  public refreshCall(): void {
    this.requestUpdate();
    void this.updateComplete.then(() => {
      this.shadowRoot?.querySelector('dees-harness-tool-card')?.refreshCall();
    });
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.emitClose();
    }
  };

  private readonly emitClose = (): void => {
    this.dispatchEvent(new CustomEvent('tool-fullscreen-close', { bubbles: false }));
  };

  public async disconnectedCallback(): Promise<void> {
    window.removeEventListener('keydown', this.handleKeydown);
    await super.disconnectedCallback();
  }
}
